import React, { useRef, useState, useEffect } from 'react';
import { useOrderStore } from '../../store/orderStore';
import { Button } from '../ui/button';
import { X, ZoomIn, ZoomOut, Image as ImageIcon, Layout, Maximize, Minimize } from 'lucide-react';
import { ProductPreview } from './ProductPreview';
import { InstallationPreview } from './InstallationPreview';

export const CenterPreview: React.FC = () => {
    const {
        uploadedImage,
        setImage,
        customPaperWidth,
        customPaperHeight,
        productOptions,
        selectedFrameProduct
    } = useOrderStore();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const previewContainerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);
    const [previewMode, setPreviewMode] = useState<'product' | 'installation'>('product');
    const [isFullScreen, setIsFullScreen] = useState(false);

    // オプション値の取得
    const borderMode = productOptions['radio_1526522940']; // フチの処理方法
    const borderTop = Number(productOptions['number_7367551325']) || 0;
    const borderBottom = Number(productOptions['number-7625311423']) || 0;
    const borderLeft = Number(productOptions['number-6908047827']) || 0;
    const borderRight = Number(productOptions['number-6909567831']) || 0;

    const specifiedImageWidth = Number(productOptions['number_0047761462']);
    const specifiedImageHeight = Number(productOptions['number-1474802223']);

    // 用紙サイズ (mm)
    const paperWidth = customPaperWidth || 210;
    const paperHeight = customPaperHeight || 297;

    // 額装シミュレーションを表示するかどうか判定
    const isFrameSelected = selectedFrameProduct && (
        selectedFrameProduct.title.includes('アルミ製フレーム') ||
        selectedFrameProduct.title.includes('木製フレーム')
    );

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setImage(event.target?.result as string);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setImage(event.target?.result as string);
            };
            reader.readAsDataURL(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleRemoveImage = () => {
        setImage(null);
        setScale(1);
    };

    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            previewContainerRef.current?.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullScreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullScreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
        };
    }, []);

    return (
        <div
            ref={previewContainerRef}
            className="flex-1 bg-gray-200 h-full flex flex-col items-center justify-center p-8 relative overflow-hidden"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
        >
            {/* ① プレビュー領域 (Preview Area) */}
            <div className="relative w-full h-full flex items-center justify-center border border-gray-400 bg-white overflow-hidden">

                {uploadedImage ? (
                    previewMode === 'product' ? (
                        <ProductPreview
                            widthMm={paperWidth}
                            heightMm={paperHeight}
                            imageUrl={uploadedImage}
                            frameType={isFrameSelected ? 'aluminum_black' : undefined}
                        />
                    ) : (
                        <InstallationPreview
                            widthMm={paperWidth}
                            heightMm={paperHeight}
                            imageUrl={uploadedImage}
                            frameType={isFrameSelected ? 'aluminum_black' : undefined}
                        />
                    )
                ) : (
                    <div className="text-center">
                        <div
                            className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-12 cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-800 mb-2">画像をアップロード</h3>
                            <p className="text-gray-500 mb-6">
                                ここに画像をドラッグ＆ドロップ<br />
                                またはクリックして選択
                            </p>
                            <Button>画像を選択する</Button>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileChange}
                        />
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={() => setImage('/sample.jpg')}
                        >
                            Debug: Load Test Image
                        </Button>
                    </div>
                )}
            </div>

            {/* Debug Button - Always visible for testing */}
            <div className="absolute top-4 right-4 z-50">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setImage('/sample.jpg')}
                >
                    Debug: Load Image
                </Button>
            </div>

            {/* コントロールボタン */}
            {uploadedImage && (
                <div className="absolute bottom-8 flex flex-col items-center space-y-4 z-10">
                    {/* モード切替 (常に表示) */}
                    <div className="bg-white p-1 rounded-lg shadow-md flex">
                        <button
                            onClick={() => setPreviewMode('product')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${previewMode === 'product' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            <ImageIcon className="w-4 h-4" />
                            <span>商品のみ</span>
                        </button>
                        <button
                            onClick={() => setPreviewMode('installation')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${previewMode === 'installation' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            <Layout className="w-4 h-4" />
                            <span>設置イメージ</span>
                        </button>
                    </div>

                    {/* 削除ボタン & 全画面ボタン */}
                    <div className="flex space-x-4 bg-white p-2 rounded-full shadow-lg">
                        <Button variant="ghost" size="icon" onClick={toggleFullScreen} className="text-gray-600 hover:text-gray-800 hover:bg-gray-100">
                            {isFullScreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                        </Button>
                        <div className="w-px bg-gray-200 mx-2" />
                        <Button variant="ghost" size="icon" onClick={handleRemoveImage} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                            <X className="w-5 h-5" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
